import { format } from "date-fns";

import {
  type LastUserChartNotesByType,
  type MergeTagsDataSource
} from "components/word-processor-field/merge-tags/merge-tags-data-source.type";
import {
  PATIENT_APPOINTMENTS_OPTIONS,
  PATIENT_CURRENT_APPOINTMENT_OPTIONS
} from "components/word-processor-field/merge-tags/merge-tags-options/patient-appointments.utils";
import { MergeTagsReplaceHandler } from "components/word-processor-field/merge-tags/merge-tags-values/merge-tags-values";
import {
  allowTableInsidePTag,
  getFormattedDate,
  getPatientSignatureMergeTagOrPlaceholder
} from "components/word-processor-field/merge-tags/merge-tags.utils";
import { APPOINTMENT_STATUSES } from "constants/appointments";
import { CONTACT_RELATIONSHIP } from "constants/patients";
import { SALE_STATUSES } from "constants/sales";
import { formatMoney, getFullName } from "utils/common";

import { getAdjustmentsTableHtml } from "./common/adjustments-table.utils";
import { getPaymentsTableHtml } from "./common/payments-table.utils";
import { getRefundsTableHtml } from "./common/refunds-table.utils";
import { getSalesItemsTableHtml } from "./common/sale-items-table.utils";

export const PATIENT_APPOINTMENTS_VALUES: MergeTagsReplaceHandler = {
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_DATE]: ({ data }) => {
    if (!data.nextAppointment?.endDate) return null;
    return getFormattedDate(data.nextAppointment?.endDate);
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_DATE_TIME]: ({ data }) => {
    if (!data.nextAppointment?.startDate) return null;
    return format(
      new Date(data.nextAppointment?.startDate),
      "EEEE MMMM dd, yyyy, 'at' h:mm aa"
    );
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_STAFF_NAME]: ({ data }) => {
    const staffMember = data.nextAppointment?.staffMember;

    if (!staffMember) return null;

    return getFullName({
      firstName: staffMember?.first_name || "",
      lastName: staffMember?.last_name || "",
      title: staffMember?.title,
      suffix: staffMember?.suffix || ""
    });
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_TYPE]: ({ data }) => {
    if (!data.nextAppointment) return null;

    return data.nextAppointment.appointmentType?.name;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_CLINIC]: ({ data }) => {
    if (!data.nextAppointment) return null;

    return `${data.nextAppointment?.clinic.practice?.name} ${data.nextAppointment?.clinic?.name}`;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_STATUS]: ({ data }) => {
    return APPOINTMENT_STATUSES.getFromCode(data.nextAppointment?.status);
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_OUTCOME_NOTES]: ({ data }) => {
    return data.nextAppointment?.outcome_notes;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_ROOM]: ({ data }) => {
    return data.nextAppointment?.room?.name;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_PHYSICIAN_REFERRAL]: ({
    data
  }) => {
    if (!data.nextAppointment?.referringPhysician) return null;

    const referringPhysician = data.nextAppointment?.referringPhysician;

    return getFullName({
      firstName: referringPhysician?.first_name || "",
      lastName: referringPhysician?.last_name || "",
      title: referringPhysician?.title,
      suffix: referringPhysician?.suffix || ""
    });
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_COMPANION_TYPE]: ({
    data
  }) => {
    return CONTACT_RELATIONSHIP.getFromCode(
      data.nextAppointment?.companionType
    );
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_COMPANION_NAME]: ({
    data
  }) => {
    return data.nextAppointment?.companionName;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_NOTES]: ({ data }) => {
    return data.nextAppointment?.notes;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_CHART_NOTES]: ({ data }) => {
    return data.nextAppointment?.chartNote?.text;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_STAFF_SIGNATURE]: ({
    data
  }) => {
    return getPatientSignatureMergeTagOrPlaceholder(
      data.nextAppointment?.staffMember?.signature
    );
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_STAFF_EMAIL]: ({ data }) => {
    return data.nextAppointment?.staffMember?.email ?? "";
  },
  [PATIENT_APPOINTMENTS_OPTIONS.NEXT_APPOINTMENT_TELEHEALTH_LINK]: ({
    data
  }) => {
    return data.nextAppointment?.zoom_join_url ?? null;
  },
  //
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_DATE]: ({ data }) => {
    if (!data.lastAppointment?.startDate) return null;
    return getFormattedDate(data.lastAppointment?.startDate);
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_DATE_TIME]: ({ data }) => {
    if (!data.lastAppointment?.startDate) return null;
    return format(
      new Date(data.lastAppointment?.startDate),
      "EEEE MMMM dd, yyyy, 'at' h:mm aa"
    );
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_STAFF_NAME]: ({ data }) => {
    const staffMember = data.lastAppointment?.staffMember;

    if (!staffMember) return null;

    return getFullName({
      firstName: staffMember?.first_name || "",
      lastName: staffMember?.last_name || "",
      title: staffMember?.title,
      suffix: staffMember?.suffix || ""
    });
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_TYPE]: ({ data }) => {
    if (!data.lastAppointment) return null;

    return data.lastAppointment.appointmentType?.name;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_CLINIC]: ({ data }) => {
    if (!data.lastAppointment) return null;

    return `${data.lastAppointment?.clinic.practice?.name} ${data.lastAppointment?.clinic?.name}`;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_STATUS]: ({ data }) => {
    return APPOINTMENT_STATUSES.getFromCode(data.lastAppointment?.status);
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_OUTCOME_NOTES]: ({ data }) => {
    return data.lastAppointment?.outcome_notes;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_ROOM]: ({ data }) => {
    return data.lastAppointment?.room?.name;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_PHYSICIAN_REFERRAL]: ({
    data
  }) => {
    if (!data.lastAppointment?.referringPhysician) return null;

    const referringPhysician = data.lastAppointment?.referringPhysician;

    return getFullName({
      firstName: referringPhysician?.first_name || "",
      lastName: referringPhysician?.last_name || "",
      title: referringPhysician?.title,
      suffix: referringPhysician?.suffix || ""
    });
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_COMPANION_TYPE]: ({
    data
  }) => {
    return CONTACT_RELATIONSHIP.getFromCode(
      data.lastAppointment?.companionType
    );
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_COMPANION_NAME]: ({
    data
  }) => {
    return data.lastAppointment?.companionName;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_NOTES]: ({ data }) => {
    return data.lastAppointment?.notes;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_CHART_NOTES]: ({ data }) => {
    return data.lastAppointment?.chartNote?.text;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.MY_LAST_CHART_NOTE]: ({ data, options }) => {
    const byType = data.lastUserChartNoteByType;
    const type = (options?.type as keyof LastUserChartNotesByType) || "any";
    return (byType?.[type] ?? byType?.any)?.text ?? null;
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_STAFF_SIGNATURE]: ({
    data
  }) => {
    return getPatientSignatureMergeTagOrPlaceholder(
      data.lastAppointment?.staffMember?.signature
    );
  },

  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_SALE_STATUS]: ({ data }) => {
    if (!data.lastAppointmentSale) return null;
    return SALE_STATUSES.getFromCode(data.lastAppointmentSale?.status);
  },

  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_SALE_TOTAL]: ({ data }) => {
    if (!data.lastAppointmentSale) return null;

    return formatMoney(Number(data.lastAppointmentSale?.total));
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_SALE_PATIENT_RESPONSIBILITY]:
    ({ data }) => {
      if (!data.lastAppointmentSale) return null;

      return formatMoney(
        Number(data.lastAppointmentSale?.patient_responsibility_total)
      );
    },

  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_SALE_PATIENT_RESPONSIBILITY_PAID]:
    ({ data }) => {
      if (!data.lastAppointmentSale) return null;

      return formatMoney(
        Number(data.lastAppointmentSale?.patient_responsibility_total_paid)
      );
    },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_SALE_PATIENT_LINE_ITEMS_COUNT]:
    ({ data }) => {
      if (!data.lastAppointmentSale) return null;

      return String(data.lastAppointmentSale?.items?.length ?? 0);
    },

  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_SALE_PATIENT_LINE_ITEMS]: ({
    data
  }) => {
    if (!data.lastAppointmentSale?.items?.length) return null;
    return allowTableInsidePTag(
      getSalesItemsTableHtml(data.lastAppointmentSale)
    );
  },

  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_SALE_PATIENT_PAYMENTS_TOTAL]:
    ({ data }) => {
      if (!data.lastAppointmentSale?.payments?.length) return null;

      return formatMoney(Number(data.lastAppointmentSale.total_paid));
    },

  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_SALE_PATIENT_PAYMENTS]: ({
    data
  }) => {
    if (!data.lastAppointmentSale?.payments?.length) return null;
    return allowTableInsidePTag(getPaymentsTableHtml(data.lastAppointmentSale));
  },

  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_SALE_PATIENT_ADJUSTMENTS_TOTAL]:
    ({ data }) => {
      return formatMoney(Number(data.lastAppointmentSale?.total_adjusted));
    },

  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_SALE_PATIENT_ADJUSTMENTS]: ({
    data
  }) => {
    if (!data.lastAppointmentSale?.adjustments?.length) return null;
    return allowTableInsidePTag(
      getAdjustmentsTableHtml(data.lastAppointmentSale)
    );
  },

  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_SALE_PATIENT_REFUNDS_TOTAL]: ({
    data
  }) => {
    return formatMoney(Number(data.lastAppointmentSale?.total_refunds));
  },
  [PATIENT_APPOINTMENTS_OPTIONS.LAST_APPOINTMENT_SALE_PATIENT_REFUNDS]: ({
    data
  }) => {
    if (!data.lastAppointmentSale?.refunds?.length) return null;
    return allowTableInsidePTag(getRefundsTableHtml(data.lastAppointmentSale));
  }
};

// SN-19877: a supervising provider fills exactly one of two roles, as Anvil fills them (and as the
// CMS-1500 claim records them): as the direct supervisor, or as the supervisor of the treating
// provider. The other role's tags print nothing.
const supervisorIn = (data: MergeTagsDataSource, direct: boolean) => {
  const appointment = data.currentAppointment;
  if (!appointment?.supervising_provider) return null;
  return Boolean(appointment.is_direct_supervisor) === direct
    ? appointment.supervising_provider
    : null;
};

const supervisorName = (data: MergeTagsDataSource, direct: boolean) => {
  const supervisor = supervisorIn(data, direct);
  if (!supervisor) return null;
  return getFullName({
    firstName: supervisor.first_name || "",
    lastName: supervisor.last_name || "",
    title: supervisor.title,
    suffix: supervisor.suffix || ""
  });
};

const supervisorSignature = (data: MergeTagsDataSource, direct: boolean) => {
  const supervisor = supervisorIn(data, direct);
  if (!supervisor) return null;
  return getPatientSignatureMergeTagOrPlaceholder(
    supervisor.signature ?? undefined
  );
};

const SUPERVISING_PROVIDER_VALUES: MergeTagsReplaceHandler = {
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_DIRECT_SUPERVISING_PROVIDER_FULL_NAME]:
    ({ data }) => supervisorName(data, true),
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_DIRECT_SUPERVISING_PROVIDER_SIGNATURE]:
    ({ data }) => supervisorSignature(data, true),
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_SUPERVISING_PROVIDER_FULL_NAME]:
    ({ data }) => supervisorName(data, false),
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_SUPERVISING_PROVIDER_SIGNATURE]:
    ({ data }) => supervisorSignature(data, false)
};

export const PATIENT_CURRENT_APPOINTMENT_VALUES: MergeTagsReplaceHandler = {
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_DATE]: ({
    data
  }) => {
    if (!data.currentAppointment?.endDate) return null;
    return getFormattedDate(data.currentAppointment?.endDate);
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_DATE_TIME]: ({
    data
  }) => {
    if (!data.currentAppointment?.startDate) return null;
    return format(
      new Date(data.currentAppointment?.startDate),
      "EEEE MMMM dd, yyyy, 'at' h:mm aa"
    );
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_STAFF_NAME]: ({
    data
  }) => {
    const staffMember = data.currentAppointment?.staffMember;
    if (!staffMember) return null;
    return getFullName({
      firstName: staffMember?.first_name || "",
      lastName: staffMember?.last_name || "",
      title: staffMember?.title,
      suffix: staffMember?.suffix || ""
    });
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_TYPE]: ({
    data
  }) => {
    if (!data.currentAppointment) return null;
    return data.currentAppointment.appointmentType?.name;
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_CLINIC]: ({
    data
  }) => {
    if (!data.currentAppointment) return null;
    return `${data.currentAppointment?.clinic.practice?.name} ${data.currentAppointment?.clinic?.name}`;
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_STATUS]: ({
    data
  }) => {
    return APPOINTMENT_STATUSES.getFromCode(data.currentAppointment?.status);
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_OUTCOME_NOTES]: ({
    data
  }) => {
    return data.currentAppointment?.outcome_notes;
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_ROOM]: ({
    data
  }) => {
    return data.currentAppointment?.room?.name;
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_PHYSICIAN_REFERRAL]:
    ({ data }) => {
      if (!data.currentAppointment?.referringPhysician) return null;
      const referringPhysician = data.currentAppointment?.referringPhysician;
      return getFullName({
        firstName: referringPhysician?.first_name || "",
        lastName: referringPhysician?.last_name || "",
        title: referringPhysician?.title,
        suffix: referringPhysician?.suffix || ""
      });
    },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_COMPANION_TYPE]: ({
    data
  }) => {
    return CONTACT_RELATIONSHIP.getFromCode(
      data.currentAppointment?.companionType
    );
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_COMPANION_NAME]: ({
    data
  }) => {
    return data.currentAppointment?.companionName;
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_NOTES]: ({
    data
  }) => {
    return data.currentAppointment?.notes;
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_CHART_NOTES]: ({
    data
  }) => {
    return data.currentAppointment?.chartNote?.text;
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_STAFF_SIGNATURE]: ({
    data
  }) => {
    return getPatientSignatureMergeTagOrPlaceholder(
      data.currentAppointment?.staffMember?.signature
    );
  },
  ...SUPERVISING_PROVIDER_VALUES,
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_TIME]: ({
    data
  }) => {
    if (!data.currentAppointment?.startDate) return null;
    return format(new Date(data.currentAppointment.startDate), "h:mm aa");
  },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_PROVIDER_LICENSE_NUMBER]:
    ({ data }) => {
      const staffMember = data.currentAppointment?.staffMember;
      return (
        staffMember?.resolved_license_number || staffMember?.license_number
      );
    },
  [PATIENT_CURRENT_APPOINTMENT_OPTIONS.CURRENT_APPOINTMENT_REFERRAL_SOURCE]: ({
    data
  }) => {
    const referralSource = data.currentAppointment?.referralSource;
    return typeof referralSource === "object" ? referralSource?.name : null;
  }
};
